import { useMemo } from 'react';
import { Activity, HardDrive } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SortableTH, Table, TBody, TD, THead, TR } from '@/components/ui/table';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, ErrorState } from '@/components/data/EmptyState';
import { useManifestSummary, useRecentHarvestFailures } from '@/hooks/useManifest';
import { useTableSort } from '@/lib/useTableSort';
import { formatNumber, formatRelative, freshnessOf, type Freshness } from '@/lib/utils';
import type { ManifestSummaryRow } from '@/types/harvest';

// Stale pipelines are the actionable signal, so they sort to the top.
const FRESHNESS_ORDER: Record<Freshness, number> = { stale: 0, recent: 1, fresh: 2 };

const FRESHNESS_VARIANT: Record<Freshness, BadgeProps['variant']> = {
  fresh: 'success',
  recent: 'default',
  stale: 'warning',
};

function sortStaleFirst(rows: ManifestSummaryRow[]): ManifestSummaryRow[] {
  return [...rows].sort((a, b) => {
    const fa = FRESHNESS_ORDER[freshnessOf(a.last_collected_at)];
    const fb = FRESHNESS_ORDER[freshnessOf(b.last_collected_at)];
    if (fa !== fb) return fa - fb;
    return (
      a.provider.localeCompare(b.provider) ||
      a.sport.localeCompare(b.sport) ||
      a.endpoint_type.localeCompare(b.endpoint_type)
    );
  });
}

export function ManifestRoute() {
  const summary = useManifestSummary();
  const failures = useRecentHarvestFailures();

  // Stale-first is the default, actionable ordering. Feeding the pre-sorted
  // rows into useTableSort with no initial key preserves it on load (dir=null
  // keeps original order) while making every column clickable.
  const baseRows = useMemo(() => sortStaleFirst(summary.data ?? []), [summary.data]);
  const coverageAccessors = useMemo(
    () => ({
      // Sort the Freshness column by its bucket rank (stale-first), not the label text.
      freshness: (r: ManifestSummaryRow) => FRESHNESS_ORDER[freshnessOf(r.last_collected_at)],
    }),
    [],
  );
  const coverage = useTableSort(baseRows, { accessors: coverageAccessors });

  const failureRows = useMemo(() => failures.data?.failures ?? [], [failures.data]);
  const failureAccessors = useMemo(
    () => ({
      job: (f: { provider: string; label: string }) => `${f.provider} ${f.label}`,
      error: (f: { errorType?: string | null; errorMessage?: string | null }) =>
        [f.errorType, f.errorMessage].filter(Boolean).join(': ') || null,
    }),
    [],
  );
  const failuresSort = useTableSort(failureRows, { accessors: failureAccessors });

  return (
    <>
      <PageHeader
        title="Harvest health"
        description="Live collection freshness (from harvest.v_manifest_summary) and recent harvest job failures (from Hangfire). Endpoints that have never collected anything don't appear yet — full API-universe coverage is tracked separately."
      />

      <Card>
        <CardHeader>
          <CardTitle>Coverage &amp; freshness</CardTitle>
          <CardDescription>
            Each collected (provider, sport, endpoint) slice and how recently it last landed an object. Stale pipelines sort to the top.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {summary.isError ? (
            <ErrorState error={summary.error} />
          ) : summary.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : !coverage.sorted.length ? (
            <EmptyState
              icon={HardDrive}
              title="No manifest rows yet"
              description="harvest.v_manifest_summary is empty. Once any harvest collects an object, its slice will appear here."
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <SortableTH label="Provider" sortKey="provider" active={coverage.sortKey === 'provider'} direction={coverage.sortDir} onSort={coverage.toggle} />
                  <SortableTH label="Sport" sortKey="sport" active={coverage.sortKey === 'sport'} direction={coverage.sortDir} onSort={coverage.toggle} />
                  <SortableTH label="Endpoint" sortKey="endpoint_type" active={coverage.sortKey === 'endpoint_type'} direction={coverage.sortDir} onSort={coverage.toggle} />
                  <SortableTH label="Objects" sortKey="object_count" active={coverage.sortKey === 'object_count'} direction={coverage.sortDir} onSort={coverage.toggle} align="right" className="text-right" />
                  <SortableTH label="Last collected" sortKey="last_collected_at" active={coverage.sortKey === 'last_collected_at'} direction={coverage.sortDir} onSort={coverage.toggle} />
                  <SortableTH label="Freshness" sortKey="freshness" active={coverage.sortKey === 'freshness'} direction={coverage.sortDir} onSort={coverage.toggle} />
                </TR>
              </THead>
              <TBody>
                {coverage.sorted.map((r) => {
                  const freshness = freshnessOf(r.last_collected_at);
                  return (
                    <TR key={`${r.provider}|${r.sport}|${r.endpoint_type}`}>
                      <TD className="capitalize">{r.provider}</TD>
                      <TD className="capitalize">{r.sport}</TD>
                      <TD className="font-mono text-xs">{r.endpoint_type}</TD>
                      <TD className="text-right tabular-nums">{formatNumber(r.object_count)}</TD>
                      <TD>{formatRelative(r.last_collected_at)}</TD>
                      <TD>
                        <Badge variant={FRESHNESS_VARIANT[freshness]}>{freshness}</Badge>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent harvest job failures</CardTitle>
          <CardDescription>
            Genius/Presto harvest &amp; sync jobs that failed in the last 24h (from Hangfire's harvest.job),
            with the exception from each job's Failed state. Benign concurrency-guard lock-timeouts
            (expected dedup, not failures) are excluded.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {failures.isError ? (
            <ErrorState error={failures.error} />
          ) : failures.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : !failures.data?.failures.length ? (
            <EmptyState
              icon={Activity}
              title="No harvest job failures"
              description="No harvest or sync jobs have failed in the last 24h."
            />
          ) : (
            <>
              <Table>
                <THead>
                  <TR>
                    <SortableTH label="Job" sortKey="job" active={failuresSort.sortKey === 'job'} direction={failuresSort.sortDir} onSort={failuresSort.toggle} />
                    <SortableTH label="Error" sortKey="error" active={failuresSort.sortKey === 'error'} direction={failuresSort.sortDir} onSort={failuresSort.toggle} />
                    <SortableTH label="Failed at" sortKey="failedAt" active={failuresSort.sortKey === 'failedAt'} direction={failuresSort.sortDir} onSort={failuresSort.toggle} />
                  </TR>
                </THead>
                <TBody>
                  {failuresSort.sorted.map((f) => (
                    <TR key={f.id}>
                      <TD>
                        {f.provider} · {f.label}
                      </TD>
                      <TD className="max-w-md">
                        {f.errorType || f.errorMessage ? (
                          <span
                            className="block truncate text-xs text-destructive"
                            title={[f.errorType, f.errorMessage].filter(Boolean).join(': ')}
                          >
                            {shortErrorType(f.errorType)}
                            {f.errorMessage ? `: ${f.errorMessage}` : ''}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TD>
                      <TD>{formatRelative(f.failedAt)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
              {failures.data.truncated && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Showing the most recent failures — more may exist; check the Hangfire screen.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}

/** Drop the namespace from a .NET exception type: `System.Net.Http.HttpRequestException` -> `HttpRequestException`. */
function shortErrorType(errorType: string | null | undefined): string {
  if (!errorType) return 'Error';
  const parts = errorType.split('.');
  return parts[parts.length - 1] || errorType;
}
